// src/layouts/ManageTeamPage.tsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Avatar,
  Chip,
  IconButton,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import teamIcon from "../../assets/settings-team/icon.svg";
import AddTeammateModal from "./AddTeammateModal";
import ConfirmDialog from "../../components/ConfirmDialog";
import { URLS } from "../../constants";
import { CompanyInfo } from "./SettingsLayout";
import { useToast } from "../../components/ToastProvider";
import api from "../../utils/api";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { normalizePhoneNumberForParsing } from "../../utils/formatPhoneNumber";
import logger from "../../utils/logger";
import { useDashboardTheme } from "../Dashboard/context/DashboardThemeContext";

// ── Types ─────────────────────────────────────────────────────────────────────

interface User {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  role: "ADMIN" | "EMPLOYEE" | "SUPERADMIN" | "BASIC";
  status: "active" | "invited";
  telephone?: string;
  gender: "male" | "female" | "other";
  language: string;
}

interface ManageTeamPageProps {
  companyInfo: CompanyInfo;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const AVATAR_PALETTES: [string, string][] = [
  ["#7956F7", "#4C3699"],
  ["#0ea5e9", "#0369a1"],
  ["#10b981", "#047857"],
  ["#f59e0b", "#b45309"],
  ["#ef4444", "#dc2626"],
  ["#ec4899", "#be185d"],
  ["#8b5cf6", "#6d28d9"],
  ["#14b8a6", "#0f766e"],
];

function getAvatarGradient(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const [a, b] = AVATAR_PALETTES[Math.abs(hash) % AVATAR_PALETTES.length];
  return `linear-gradient(135deg, ${a} 0%, ${b} 100%)`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ManageTeamPage({ companyInfo }: ManageTeamPageProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = useDashboardTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showToast } = useToast();

  function getUserRoleFromToken():
    | "ADMIN"
    | "EMPLOYEE"
    | "SUPERADMIN"
    | "BASIC"
    | undefined {
    const raw = localStorage.getItem("authUser");
    if (!raw) return undefined;
    try {
      const user = JSON.parse(raw) as { role?: string };
      return user.role as
        | "ADMIN"
        | "EMPLOYEE"
        | "SUPERADMIN"
        | "BASIC"
        | undefined;
    } catch {
      logger.error("invalid authUser in localStorage");
      return undefined;
    }
  }
  const userRole = getUserRoleFromToken();
  const canManageUsers = userRole === "ADMIN" || userRole === "SUPERADMIN";

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [deleting, setDeleting] = useState<User | null>(null);

  // Reset password state
  const [resetTarget, setResetTarget] = useState<User | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [showResetPw, setShowResetPw] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const closeResetDialog = () => {
    setResetTarget(null);
    setResetPassword("");
    setResetConfirm("");
    setShowResetPw(false);
    setShowResetConfirm(false);
  };

  const handleResetPassword = async () => {
    if (
      !resetTarget ||
      resetPassword !== resetConfirm ||
      resetPassword.length < 8
    )
      return;
    setResetLoading(true);
    try {
      const res = await api.post(`${URLS.CLIENT_URL}/api/user/reset-password`, {
        userId: resetTarget.id,
        newPassword: resetPassword,
      });
      if (res.status !== 200) throw new Error("Failed to reset password");
      closeResetDialog();
      showToast("Senha redefinida com sucesso", "success");
    } catch (err: unknown) {
      logger.error("handleResetPassword", err);
      showToast("Erro ao redefinir senha", "error");
    } finally {
      setResetLoading(false);
    }
  };

  function formatPhone(phone: string): string {
    if (!phone) return "";
    try {
      const normalized = normalizePhoneNumberForParsing(phone);
      if (!normalized) return phone;
      const parsed = parsePhoneNumberFromString(normalized);
      if (parsed) return parsed.formatInternational();
    } catch {}
    return phone;
  }

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${URLS.CLIENT_URL}/api/users-by-company`);
      if (res.status !== 200) throw new Error("Error when seeking users");
      const data = res.data;
      const mapped: User[] = data.map((u: any) => ({
        id: u.id,
        firstname: u.firstname,
        lastname: u.lastname,
        email: u.email,
        role: u.role,
        status: u.is_active ? "active" : "invited",
        telephone: Array.isArray(u.telephone) ? u.telephone[0] : u.telephone,
        gender: u.gender,
        language: u.language,
      }));
      setUsers(mapped);
    } catch (err: unknown) {
      logger.error("fetchUsers", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreate = async (data: any) => {
    try {
      const res = await api.post(`${URLS.CLIENT_URL}/api/signup`, {
        ...data,
        company_id: companyInfo.id,
        cpf: "00000000000",
        is_active: true,
      });
      if (res.status !== 201) throw new Error("Failed to create user");
      await fetchUsers();
      showToast(t("toast.userCreated"), "success");
    } catch (err: unknown) {
      logger.error("handleCreate", err);
      showToast(t("toast.errorCreate"), "error");
    }
  };

  const handleUpdate = async (data: any) => {
    if (!editing) return;
    const payload = {
      id: editing.id,
      firstName: data.firstname,
      lastName: data.lastname,
      email: data.email,
      phone: data.telephone,
      gender: data.gender,
      role: data.role,
      language: data.language,
    };
    try {
      const res = await api.put(`${URLS.CLIENT_URL}/api/user`, payload);
      if (res.status !== 200) throw new Error("Failed to update user");
      await fetchUsers();
      setEditing(null);
      showToast(t("toast.userUpdated"), "success");
    } catch (err: unknown) {
      logger.error("handleUpdate", err);
      showToast(t("toast.errorUpdate"), "error");
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      const res = await api.delete(`${URLS.CLIENT_URL}/api/user`, {
        headers: { "Content-Type": "application/json" },
        data: { id: deleting.id },
      });
      if (res.status !== 200) throw new Error("Failed to delete user");
      await fetchUsers();
      setDeleting(null);
      showToast(t("toast.userDeleted"), "success");
    } catch (err: unknown) {
      logger.error("handleDelete", err);
      showToast(t("toast.errorDelete"), "error");
    }
  };

  // ── Derived styles ──────────────────────────────────────────────────────────

  const bg = isDark ? "rgba(22,22,30,0.95)" : "#ffffff";
  const border = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)";
  const skeletonBg = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)";
  const dividerColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)";
  const textPrimary = isDark ? "#ffffff" : "#111827";
  const textMuted = isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)";
  const textSecondary = isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.55)";

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ px: isMobile ? 2 : 4, py: 3 }}>
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              background: isDark
                ? "rgba(121,86,247,0.15)"
                : "rgba(121,86,247,0.08)",
              border: `1px solid ${isDark ? "rgba(121,86,247,0.3)" : "rgba(121,86,247,0.18)"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <img src={teamIcon} alt="Team" style={{ width: 24, height: 24 }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ color: textPrimary, lineHeight: 1.2 }}
            >
              {t("manageTeam.title")}
            </Typography>
            {!loading && (
              <Typography variant="body2" sx={{ color: textMuted, mt: 0.3 }}>
                {users.length}{" "}
                {users.length === 1 ? "membro na equipe" : "membros na equipe"}
              </Typography>
            )}
          </Box>
        </Box>

        {canManageUsers && (
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
            sx={{
              background: "linear-gradient(135deg, #7956F7 0%, #4C3699 100%)",
              color: "#ffffff",
              borderRadius: "12px",
              px: 3,
              py: 1.2,
              fontWeight: 600,
              textTransform: "none",
              fontSize: "0.9rem",
              boxShadow: "0 4px 14px rgba(121,86,247,0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #8a68ff 0%, #5d45b8 100%)",
                boxShadow: "0 6px 20px rgba(121,86,247,0.5)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
            }}
          >
            {t("manageTeam.button.add")}
          </Button>
        )}
      </Box>

      {/* ── Loading skeletons ── */}
      {loading && (
        <Grid container spacing={2.5} columns={{ xs: 1, sm: 2, md: 3 }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid key={i} size={{ xs: 1 }}>
              <Box
                sx={{
                  borderRadius: "16px",
                  overflow: "hidden",
                  border: `1px solid ${border}`,
                }}
              >
                <Skeleton
                  variant="rectangular"
                  height={4}
                  sx={{ bgcolor: skeletonBg }}
                />
                <Box sx={{ p: 2.5, background: bg }}>
                  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <Skeleton
                      variant="rectangular"
                      width={52}
                      height={52}
                      sx={{
                        borderRadius: "14px",
                        bgcolor: skeletonBg,
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton
                        variant="text"
                        sx={{ bgcolor: skeletonBg, mb: 0.5 }}
                      />
                      <Skeleton
                        variant="text"
                        width="60%"
                        sx={{ bgcolor: skeletonBg }}
                      />
                    </Box>
                  </Box>
                  <Skeleton
                    variant="text"
                    sx={{ bgcolor: skeletonBg, mb: 0.5 }}
                  />
                  <Skeleton
                    variant="text"
                    width="70%"
                    sx={{ bgcolor: skeletonBg }}
                  />
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Empty state ── */}
      {!loading && users.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: 10,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "20px",
              background: isDark
                ? "rgba(121,86,247,0.12)"
                : "rgba(121,86,247,0.07)",
              border: `1px solid ${isDark ? "rgba(121,86,247,0.25)" : "rgba(121,86,247,0.15)"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <GroupsOutlinedIcon
              sx={{
                fontSize: 36,
                color: isDark ? "rgba(167,139,250,0.7)" : "#7956F7",
              }}
            />
          </Box>
          <Typography variant="h6" fontWeight={600} sx={{ color: textPrimary }}>
            {t("manageTeam.empty")}
          </Typography>
          <Typography variant="body2" sx={{ color: textMuted, maxWidth: 320 }}>
            Adicione membros à equipe para que eles possam acessar a plataforma.
          </Typography>
        </Box>
      )}

      {/* ── Cards grid ── */}
      {!loading && users.length > 0 && (
        <Grid container spacing={2.5} columns={{ xs: 1, sm: 2, md: 3 }}>
          {users.map((user, index) => {
            const isSuperAdmin = user.role === "SUPERADMIN";
            const isAdmin = user.role === "ADMIN";
            const isActive = user.status === "active";

            const roleLabel = isSuperAdmin
              ? "Gestor da Plataforma"
              : isAdmin
                ? t("manageTeam.role.admin")
                : t("manageTeam.role.employee");

            const roleAccent = isSuperAdmin
              ? { from: "#f59e0b", to: "#b45309" }
              : isAdmin
                ? { from: "#7956F7", to: "#4C3699" }
                : { from: "#10b981", to: "#047857" };

            const roleChipBg = isSuperAdmin
              ? isDark
                ? "rgba(245,158,11,0.18)"
                : "rgba(245,158,11,0.1)"
              : isAdmin
                ? isDark
                  ? "rgba(121,86,247,0.18)"
                  : "rgba(121,86,247,0.09)"
                : isDark
                  ? "rgba(16,185,129,0.18)"
                  : "rgba(16,185,129,0.09)";

            const roleChipColor = isSuperAdmin
              ? isDark
                ? "#fbbf24"
                : "#b45309"
              : isAdmin
                ? isDark
                  ? "#a78bfa"
                  : "#6d40e8"
                : isDark
                  ? "#34d399"
                  : "#047857";

            const roleChipBorder = isSuperAdmin
              ? "rgba(245,158,11,0.35)"
              : isAdmin
                ? "rgba(121,86,247,0.28)"
                : "rgba(16,185,129,0.28)";

            return (
              <Grid key={user.id} size={{ xs: 1 }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.32,
                    delay: index * 0.06,
                    ease: [0.4, 0, 0.2, 1],
                  }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  style={{ height: "100%" }}
                >
                  <Paper
                    sx={{
                      p: 0,
                      overflow: "hidden",
                      borderRadius: "16px",
                      background: bg,
                      border: `1px solid ${border}`,
                      boxShadow: isDark
                        ? "0 4px 24px rgba(0,0,0,0.25)"
                        : "0 2px 10px rgba(0,0,0,0.06)",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      transition: "box-shadow 0.2s",
                      "&:hover": {
                        boxShadow: isDark
                          ? "0 8px 32px rgba(0,0,0,0.4)"
                          : "0 8px 24px rgba(0,0,0,0.1)",
                      },
                    }}
                  >
                    {/* Top accent band */}
                    <Box
                      sx={{
                        height: 3,
                        background: `linear-gradient(90deg, ${roleAccent.from} 0%, ${roleAccent.to} 100%)`,
                        flexShrink: 0,
                      }}
                    />

                    <Box
                      sx={{
                        p: 2.5,
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.75,
                      }}
                    >
                      {/* ── Avatar + name + role + status ── */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.75,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 52,
                            height: 52,
                            background: getAvatarGradient(user.firstname),
                            color: "#ffffff",
                            fontWeight: 700,
                            fontSize: "1.25rem",
                            borderRadius: "14px",
                            flexShrink: 0,
                          }}
                        >
                          {user.firstname.charAt(0).toUpperCase()}
                        </Avatar>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            fontWeight={700}
                            noWrap
                            sx={{
                              color: textPrimary,
                              lineHeight: 1.3,
                              fontSize: "0.95rem",
                            }}
                          >
                            {user.firstname} {user.lastname}
                          </Typography>

                          <Chip
                            label={roleLabel}
                            size="small"
                            sx={{
                              mt: 0.5,
                              height: 20,
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              textTransform: "none",
                              background: roleChipBg,
                              color: roleChipColor,
                              border: `1px solid ${roleChipBorder}`,
                              "& .MuiChip-label": { px: 1 },
                            }}
                          />
                        </Box>

                        {/* Status dot */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.6,
                            flexShrink: 0,
                            mt: 0.25,
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: isActive ? "#22c55e" : "#f59e0b",
                              boxShadow: isActive
                                ? "0 0 6px rgba(34,197,94,0.55)"
                                : "0 0 6px rgba(245,158,11,0.55)",
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.67rem",
                              color: isActive
                                ? isDark
                                  ? "#4ade80"
                                  : "#16a34a"
                                : isDark
                                  ? "#fbbf24"
                                  : "#b45309",
                              fontWeight: 600,
                            }}
                          >
                            {isActive ? "Ativo" : "Convidado"}
                          </Typography>
                        </Box>
                      </Box>

                      {/* ── Contact info ── */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.6,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <EmailOutlinedIcon
                            sx={{
                              fontSize: 13,
                              color: textMuted,
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{ color: textSecondary, fontSize: "0.78rem" }}
                          >
                            {user.email}
                          </Typography>
                        </Box>
                        {user.telephone && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <PhoneOutlinedIcon
                              sx={{
                                fontSize: 13,
                                color: textMuted,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{ color: textSecondary, fontSize: "0.78rem" }}
                            >
                              {formatPhone(user.telephone)}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* ── Actions ── */}
                      {canManageUsers && (
                        <Box
                          sx={{
                            mt: "auto",
                            pt: 1.5,
                            display: "flex",
                            gap: 0.75,
                            justifyContent: "flex-end",
                            borderTop: `1px solid ${dividerColor}`,
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => setResetTarget(user)}
                            title="Redefinir senha"
                            sx={{
                              borderRadius: "8px",
                              width: 32,
                              height: 32,
                              color: textMuted,
                              "&:hover": {
                                background: isDark
                                  ? "rgba(245,158,11,0.15)"
                                  : "rgba(245,158,11,0.1)",
                                color: "#f59e0b",
                              },
                              transition: "all 0.2s",
                            }}
                          >
                            <LockResetIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditing(user);
                              setModalOpen(true);
                            }}
                            title="Editar usuário"
                            sx={{
                              borderRadius: "8px",
                              width: 32,
                              height: 32,
                              color: textMuted,
                              "&:hover": {
                                background: isDark
                                  ? "rgba(121,86,247,0.15)"
                                  : "rgba(121,86,247,0.08)",
                                color: "#7956F7",
                              },
                              transition: "all 0.2s",
                            }}
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => setDeleting(user)}
                            title="Excluir usuário"
                            sx={{
                              borderRadius: "8px",
                              width: 32,
                              height: 32,
                              color: textMuted,
                              "&:hover": {
                                background: "rgba(239,68,68,0.1)",
                                color: "#ef4444",
                              },
                              transition: "all 0.2s",
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ── Reset password dialog ── */}
      <Dialog
        open={!!resetTarget}
        onClose={closeResetDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: isDark ? "#1e1e2e" : "#ffffff",
            border: isDark ? "1px solid rgba(255,255,255,0.08)" : "none",
            borderRadius: "16px",
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            color: isDark ? "#fff" : "#111",
            fontWeight: 700,
            fontSize: "1rem",
            borderBottom: `1px solid ${dividerColor}`,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "9px",
                background: isDark
                  ? "rgba(245,158,11,0.15)"
                  : "rgba(245,158,11,0.1)",
                border: "1px solid rgba(245,158,11,0.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LockResetIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
            </Box>
            <Box>
              <Typography
                fontWeight={700}
                sx={{ color: isDark ? "#fff" : "#111", lineHeight: 1.2 }}
              >
                Redefinir Senha
              </Typography>
              {resetTarget && (
                <Typography variant="caption" sx={{ color: textMuted }}>
                  {resetTarget.firstname} {resetTarget.lastname}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Nova senha"
              type={showResetPw ? "text" : "password"}
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              helperText="Mínimo 8 caracteres"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowResetPw((v) => !v)}
                      sx={{ color: textMuted }}
                    >
                      {showResetPw ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& label": { color: textMuted },
                "& label.Mui-focused": { color: "#f59e0b" },
                "& input": { color: textPrimary },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: isDark
                      ? "rgba(255,255,255,0.15)"
                      : "rgba(0,0,0,0.2)",
                  },
                  "&:hover fieldset": {
                    borderColor: isDark
                      ? "rgba(255,255,255,0.3)"
                      : "rgba(0,0,0,0.35)",
                  },
                  "&.Mui-focused fieldset": { borderColor: "#f59e0b" },
                },
                "& .MuiFormHelperText-root": { color: textMuted },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Confirmar nova senha"
              type={showResetConfirm ? "text" : "password"}
              value={resetConfirm}
              onChange={(e) => setResetConfirm(e.target.value)}
              error={resetConfirm.length > 0 && resetPassword !== resetConfirm}
              helperText={
                resetConfirm.length > 0 && resetPassword !== resetConfirm
                  ? "Senhas não coincidem"
                  : ""
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowResetConfirm((v) => !v)}
                      sx={{ color: textMuted }}
                    >
                      {showResetConfirm ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& label": { color: textMuted },
                "& label.Mui-focused": { color: "#f59e0b" },
                "& input": { color: textPrimary },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: isDark
                      ? "rgba(255,255,255,0.15)"
                      : "rgba(0,0,0,0.2)",
                  },
                  "&:hover fieldset": {
                    borderColor: isDark
                      ? "rgba(255,255,255,0.3)"
                      : "rgba(0,0,0,0.35)",
                  },
                  "&.Mui-focused fieldset": { borderColor: "#f59e0b" },
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2.5,
            pt: 1.5,
            borderTop: `1px solid ${dividerColor}`,
            gap: 1,
          }}
        >
          <Button
            onClick={closeResetDialog}
            sx={{
              color: textMuted,
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 600,
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={
              resetLoading ||
              resetPassword.length < 8 ||
              resetPassword !== resetConfirm
            }
            onClick={handleResetPassword}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(135deg, #f59e0b 0%, #b45309 100%)",
              color: "#fff",
              boxShadow: "0 3px 10px rgba(245,158,11,0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #fbbf24 0%, #d97706 100%)",
                boxShadow: "0 5px 16px rgba(245,158,11,0.5)",
              },
              "&:disabled": {
                background: isDark
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.08)",
                color: textMuted,
                boxShadow: "none",
              },
              transition: "all 0.2s",
            }}
          >
            {resetLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  component="span"
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    border: "2px solid currentColor",
                    borderTopColor: "transparent",
                    animation: "spin 0.7s linear infinite",
                    "@keyframes spin": { to: { transform: "rotate(360deg)" } },
                  }}
                />
                Redefinindo...
              </Box>
            ) : (
              "Redefinir Senha"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Modal add/edit ── */}
      <AddTeammateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={editing ? handleUpdate : handleCreate}
        companyId={companyInfo.id}
        initialData={
          editing
            ? {
                ...editing,
                telephone: editing.telephone,
                gender: editing.gender,
                language: editing.language,
              }
            : undefined
        }
      />

      {/* ── Confirm delete ── */}
      <ConfirmDialog
        open={!!deleting}
        title={t("manageTeam.confirm.title")}
        message={t("manageTeam.confirm.message", {
          name: deleting ? `${deleting.firstname} ${deleting.lastname}` : "",
        })}
        onConfirm={handleDelete}
        onClose={() => setDeleting(null)}
      />
    </Box>
  );
}
